// skins.ts — data for every hole skin. Pure (no Three.js) so the shop, the
// profile and the tests can use it; holeVisual.ts turns a skin into 3D.

export type Rarity = "common" | "rare" | "epic" | "legendary";

/** What lines the rim. Each kind is a small instanced mesh repeated around it. */
export type DecoKind = "none" | "studs" | "spikes" | "crystals" | "petals" | "pepperoni" | "sprinkles" | "bolts" | "bubbles" | "orbs";
/** Shader used for the inside wall of the hole. */
export type InnerKind = "dark" | "galaxy" | "lava" | "grid" | "swirl" | "slime";
/** Ambient particles emitted around the rim. */
export type ParticleKind = "none" | "embers" | "snow" | "sparkle" | "bubbles" | "stars" | "leaves" | "sparks" | "hearts";

export interface SkinDefinition {
  id: string;
  /** English name; localised names live in i18n under skin.<id>. */
  label: string;
  rarity: Rarity;
  /** Coin price; 0 = free. */
  price: number;
  /** Main rim colour, also used for HUD dots and the leaderboard. */
  color: string;
  rim: {
    color: string;
    emissive: string;
    emissiveIntensity: number;
    metalness: number;
    roughness: number;
    /** Tube thickness relative to the hole radius. */
    thickness: number;
    /** Optional second, thinner ring just outside the main rim. */
    outer?: string;
  };
  deco: { kind: DecoKind; count: number; color: string; accent?: string; spin: number };
  inner: { kind: InnerKind; a: string; b: string };
  particles: { kind: ParticleKind; color: string; rate: number };
  /** Ground glow ring colour + strength. */
  glow: { color: string; strength: number };
  /** Colour of the short trail left while moving. */
  trail: string;
}

export const RARITY_COLOR: Record<Rarity, string> = {
  common: "#9fb0c6",
  rare: "#4fa3ff",
  epic: "#b26bff",
  legendary: "#ffbf3c",
};

export const SKINS: readonly SkinDefinition[] = [
  {
    id: "noob_black", label: "Obsidian", rarity: "common", price: 0, color: "#3a4458",
    rim: { color: "#232a38", emissive: "#4a5a78", emissiveIntensity: 0.12, metalness: 0.75, roughness: 0.3, thickness: 0.075 },
    deco: { kind: "none", count: 0, color: "#000", spin: 0 },
    inner: { kind: "dark", a: "#05070d", b: "#141a26" },
    particles: { kind: "none", color: "#fff", rate: 0 },
    glow: { color: "#7a8cab", strength: 0.18 },
    trail: "#8796b0",
  },
  {
    id: "noob_green", label: "Toxic Slime", rarity: "common", price: 250, color: "#36d17c",
    rim: { color: "#2fd46f", emissive: "#2fd46f", emissiveIntensity: 0.45, metalness: 0.05, roughness: 0.25, thickness: 0.1 },
    deco: { kind: "bubbles", count: 14, color: "#7dffb0", spin: 0.35 },
    inner: { kind: "slime", a: "#04140a", b: "#1fbf5a" },
    particles: { kind: "bubbles", color: "#8affb8", rate: 9 },
    glow: { color: "#3dff8c", strength: 0.35 },
    trail: "#47f08e",
  },
  {
    id: "donut", label: "Sugar Donut", rarity: "common", price: 400, color: "#ff8bd0",
    rim: { color: "#f7a1d2", emissive: "#ff7cc4", emissiveIntensity: 0.12, metalness: 0, roughness: 0.55, thickness: 0.16, outer: "#c98b4f" },
    deco: { kind: "sprinkles", count: 46, color: "#fff27a", accent: "#62c6ff", spin: 0.12 },
    inner: { kind: "dark", a: "#1a0b06", b: "#5a2f17" },
    particles: { kind: "hearts", color: "#ff9bd6", rate: 3 },
    glow: { color: "#ff9bd6", strength: 0.22 },
    trail: "#ffb3df",
  },
  {
    id: "pro_red", label: "Lava Core", rarity: "rare", price: 900, color: "#ff5a2a",
    rim: { color: "#3a1208", emissive: "#ff4a12", emissiveIntensity: 0.9, metalness: 0.2, roughness: 0.7, thickness: 0.12 },
    deco: { kind: "spikes", count: 12, color: "#2a0d07", accent: "#ff7a2a", spin: 0.18 },
    inner: { kind: "lava", a: "#2a0500", b: "#ff6a1a" },
    particles: { kind: "embers", color: "#ffae42", rate: 16 },
    glow: { color: "#ff5a1f", strength: 0.55 },
    trail: "#ff7a2a",
  },
  {
    id: "frost", label: "Frostbite", rarity: "rare", price: 1100, color: "#9fe8ff",
    rim: { color: "#d8f6ff", emissive: "#7fdcff", emissiveIntensity: 0.35, metalness: 0.1, roughness: 0.12, thickness: 0.09 },
    deco: { kind: "crystals", count: 10, color: "#bff1ff", accent: "#ffffff", spin: -0.1 },
    inner: { kind: "swirl", a: "#06121c", b: "#5fcbff" },
    particles: { kind: "snow", color: "#ffffff", rate: 12 },
    glow: { color: "#8fe4ff", strength: 0.4 },
    trail: "#c9f3ff",
  },
  {
    id: "pizza", label: "Pizza Party", rarity: "rare", price: 1300, color: "#ffb347",
    rim: { color: "#e5a456", emissive: "#d48a33", emissiveIntensity: 0.08, metalness: 0, roughness: 0.8, thickness: 0.17, outer: "#ffd75e" },
    deco: { kind: "pepperoni", count: 9, color: "#c7372a", accent: "#7a1f15", spin: 0.08 },
    inner: { kind: "dark", a: "#120402", b: "#5c1a0c" },
    particles: { kind: "leaves", color: "#5fbf4a", rate: 4 },
    glow: { color: "#ffcf6a", strength: 0.25 },
    trail: "#ffcf6a",
  },
  {
    id: "pro_blue", label: "Neon Grid", rarity: "epic", price: 2200, color: "#29e6ff",
    rim: { color: "#0a1426", emissive: "#29e6ff", emissiveIntensity: 1.1, metalness: 0.6, roughness: 0.2, thickness: 0.06, outer: "#ff3ec8" },
    deco: { kind: "orbs", count: 6, color: "#29e6ff", accent: "#ff3ec8", spin: 0.9 },
    inner: { kind: "grid", a: "#02030a", b: "#29e6ff" },
    particles: { kind: "sparks", color: "#7af4ff", rate: 10 },
    glow: { color: "#29e6ff", strength: 0.6 },
    trail: "#ff3ec8",
  },
  {
    id: "pro_purple", label: "Galaxy", rarity: "epic", price: 2800, color: "#9c62ff",
    rim: { color: "#1b0f3a", emissive: "#8a4dff", emissiveIntensity: 0.7, metalness: 0.5, roughness: 0.25, thickness: 0.085 },
    deco: { kind: "orbs", count: 3, color: "#ffe9a8", accent: "#b48cff", spin: 0.6 },
    inner: { kind: "galaxy", a: "#05021a", b: "#b07bff" },
    particles: { kind: "stars", color: "#e8d6ff", rate: 12 },
    glow: { color: "#9c62ff", strength: 0.5 },
    trail: "#c3a0ff",
  },
  {
    id: "garden", label: "Bloom", rarity: "epic", price: 3200, color: "#ff7eb6",
    rim: { color: "#3f8f3a", emissive: "#2e6b2b", emissiveIntensity: 0.1, metalness: 0, roughness: 0.7, thickness: 0.1 },
    deco: { kind: "petals", count: 16, color: "#ff8fc2", accent: "#ffe46b", spin: 0.15 },
    inner: { kind: "swirl", a: "#071207", b: "#4bbf5a" },
    particles: { kind: "leaves", color: "#ffa8d2", rate: 7 },
    glow: { color: "#ff9cc9", strength: 0.3 },
    trail: "#ffb6d8",
  },
  {
    id: "storm", label: "Thunderstorm", rarity: "epic", price: 3600, color: "#8ab4ff",
    rim: { color: "#2b3446", emissive: "#6f9dff", emissiveIntensity: 0.5, metalness: 0.4, roughness: 0.45, thickness: 0.1 },
    deco: { kind: "bolts", count: 8, color: "#fff27a", accent: "#ffffff", spin: -0.7 },
    inner: { kind: "swirl", a: "#05070f", b: "#6f9dff" },
    particles: { kind: "sparks", color: "#fff6a0", rate: 14 },
    glow: { color: "#7aa6ff", strength: 0.45 },
    trail: "#aac6ff",
  },
  {
    id: "hacker_gold", label: "Golden Vault", rarity: "legendary", price: 6000, color: "#ffc84a",
    rim: { color: "#ffcf4a", emissive: "#ff9d1a", emissiveIntensity: 0.25, metalness: 1, roughness: 0.18, thickness: 0.11, outer: "#fff1b0" },
    deco: { kind: "studs", count: 18, color: "#fff4c2", accent: "#c98a12", spin: 0.1 },
    inner: { kind: "swirl", a: "#120a00", b: "#ffbf3c" },
    particles: { kind: "sparkle", color: "#ffe9a0", rate: 14 },
    glow: { color: "#ffc84a", strength: 0.55 },
    trail: "#ffe08a",
  },
  {
    id: "singularity", label: "Singularity", rarity: "legendary", price: 9000, color: "#ff4f8b",
    rim: { color: "#0b0610", emissive: "#ff4f8b", emissiveIntensity: 1.3, metalness: 0.8, roughness: 0.1, thickness: 0.05, outer: "#5ad1ff" },
    deco: { kind: "orbs", count: 8, color: "#ffffff", accent: "#ff4f8b", spin: 1.6 },
    inner: { kind: "galaxy", a: "#000000", b: "#ff4f8b" },
    particles: { kind: "stars", color: "#ffc2d9", rate: 18 },
    glow: { color: "#ff4f8b", strength: 0.75 },
    trail: "#5ad1ff",
  },
];

const BY_ID = new Map(SKINS.map((skin) => [skin.id, skin]));

export function skinById(id: string | undefined): SkinDefinition {
  return (id && BY_ID.get(id)) || SKINS[0]!;
}

/** Bots wear a varied but stable set of skins (never the player's). */
export function botSkin(index: number, avoid?: string): SkinDefinition {
  const pool = SKINS.filter((skin) => skin.id !== avoid && skin.id !== "noob_black");
  return pool[(index * 5 + 3) % pool.length]!;
}
